/**
 * Where a rejected settings write goes to be seen.
 *
 * `100-settings.md` §6: "The client maps `violations[].field` back to the control by its dotted
 * key and renders the message inline beneath it. The dotted key is the contract between the
 * two." The violation arrives at DELIVERY time, in the outbox bridge — which is three routes
 * away from the settings screen and may fire when that screen is not even mounted. So it lands
 * here, in a tiny external store, and the screen reads it when it next renders. Same shape as
 * `features/calendar/eventDialogStore.ts`, and for the same reason.
 *
 * Keyed by the DOTTED key (`ai.maxTokens`), never by the nested path — the dotted key is what
 * the server sends and what the control declares.
 */
import { useSyncExternalStore } from 'react';

export type SettingsViolations = Readonly<Record<string, string>>;

const EMPTY: SettingsViolations = {};

const listeners = new Set<() => void>();
let violations: SettingsViolations = EMPTY;

function emit(next: SettingsViolations): void {
  violations = next;
  for (const listener of listeners) listener();
}

export function setSettingsViolations(next: SettingsViolations): void {
  emit(next);
}

/** Called by the control the moment the user changes it again — the error is about the old value. */
export function clearSettingsViolation(field: string): void {
  if (!(field in violations)) return;
  const next = { ...violations };
  delete next[field];
  emit(next);
}

export function clearSettingsViolations(): void {
  if (violations === EMPTY) return;
  emit(EMPTY);
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function snapshot(): SettingsViolations {
  return violations;
}

export function useSettingsViolations(): SettingsViolations {
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}
